import { iframeUrl, sdkVersion } from './config/config'

function searchParamsRecord() {
  const search = window.location.search

  const params = new URLSearchParams(search)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    result[key] = value
  })
  return result
}

export class AcimaLauncher {
  private _merchantId: ID
  private _iframeUrl: string
  private _iframeOrigin: string
  private _selfOrigin: string
  private _parentOrigin: string
  private _ancestorOrigins: string[]
  private _additionalOrigins: string[]
  private _parentElem: HTMLElement
  private _launchAsModal: boolean
  private _containerMode: ContainerMode
  private _showDefaultHeader: boolean
  private _userCanClose: boolean
  private _activeMessageHandler: MessageListener | null = null
  private _iframeElement: HTMLIFrameElement | null = null
  private _platform: Platform | undefined
  private _source: Source | undefined
  private _phpVersion: string | null | undefined
  private _pluginVersion: string | null | undefined
  private _databaseVersion: string | null | undefined
  private _trustevSessionId: string | undefined
  private _neuroidSessionId: NeuroidSession | undefined
  private _analyticCorrelationId: string | undefined

  constructor(config: ClientOptions) {
    this._merchantId = config.merchantId
    this._iframeUrl = config.iframeUrl || iframeUrl
    this._iframeOrigin = this._origin(this._iframeUrl)
    this._selfOrigin = this._origin(location.href);
    this._parentOrigin = this._origin(document.referrer);
    this._ancestorOrigins = config.ancestorOrigins ?? []
    this._platform = config.platform
    this._source = config.source
    this._phpVersion = config.phpVersion
    this._pluginVersion = config.pluginVersion
    this._databaseVersion = config.databaseVersion
    this._trustevSessionId = config.trustevSessionId
    this._neuroidSessionId = config.neuroidSessionId
    this._analyticCorrelationId = config.analyticCorrelationId
    this._additionalOrigins =
      this._compact([this._selfOrigin, this._parentOrigin, ...this._ancestorOrigins])

    if (config.iframeContainer == null) {
      this._launchAsModal = true
      this._userCanClose = true
      this._showDefaultHeader = true
      this._containerMode = "modal-overlay"
      this._parentElem = document.body
    } else {
      this._launchAsModal = false
      this._userCanClose = false
      this._showDefaultHeader = false
      this._containerMode = "embedded"

      if (config.iframeContainer instanceof HTMLElement) {
        this._parentElem = config.iframeContainer
      } else if (typeof config.iframeContainer === 'string') {
        let parent = document.getElementById(config.iframeContainer)
        if (parent instanceof HTMLElement) {
          this._parentElem = parent
        }
      }

      // If an element was specified we need to have found it by now
      if (!this._parentElem) {
        throw new Error("Acima iframe container does not exist")
      }
    }
  }

  private createOrderComplete() {
    this._postMessage({
      type: 'CREATE_ORDER_COMPLETE',
    })
  }

  closeIframe() {
    this._closeIframe()
  }

  checkout(opts: CheckoutOptions): Promise<CheckoutResponse> {
    return new Promise((resolve, reject) => {
      this._openIframe({
        onCheckoutSuccessful: (result) =>
          resolve({
            leaseId: result.leaseId,
            checkoutToken: result.checkoutToken,
            leaseNumber: result.leaseNumber,
            applicationId: result.applicationId,
            virtualCard: result.virtualCard,
            createOrderComplete: this.createOrderComplete.bind(this),
            approvalAmount: result.approvalAmount
          }),
        onInitialPayment: (result) => {
          if (typeof opts.onInitialPayment == "function") {
            opts.onInitialPayment(result)
          }
        },
        onLeaseAgreementSigned: (result) => {
          if (typeof opts.onLeaseAgreementSigned == "function") {
            opts.onLeaseAgreementSigned(result)
          }
        },
        onPreapprovalSuccessful: () => { },
        onPrequalSuccessful: () => { },
        onConditionSatisfiedSuccessful: () => { },
        onClose: (result) => {
          this._closeIframe()
          reject({ code: 'CheckoutInterrupted', message: 'Checkout was interrupted' })
          if (typeof opts.onClose == "function") {
            opts.onClose(result)
          }
        },
        onError: (e: ClientError) => reject(e),
        onResize: (e) => {
          if (typeof opts.onResize == "function") {
            opts.onResize({ width: e.width, height: e.height })
          }
        },
        onPageTransition: (result) => {
          if (typeof opts.onPageTransition == "function") {
            opts.onPageTransition({ page: result.page })
          }
        },
        onDenied: (e) => {
          if (typeof opts.onDenied == "function") {
            opts.onDenied(e)
          }
        },
        onCancelled: (e) => {
          if (typeof opts.onCancelled == "function") {
            opts.onCancelled(e)
          }
        },
        onIncomplete: (e) => {
          if (typeof opts.onIncomplete == "function") {
            opts.onIncomplete(e)
          }
        },
        onReady: () => this._postMessage({
          type: 'ACIMA_ECOM_IFRAME_INITIALIZE',
          mode: 'Checkout',
          enablePolling: opts.enablePolling ?? true,
          containerMode: this._containerMode,
          userCanClose: opts.userCanClose ?? this._userCanClose,
          showDefaultHeader: opts.showDefaultHeader ?? this._showDefaultHeader,
          contractId: opts.contractId,
          authToken: opts.authToken,
          impersonatedSession: opts.impersonatedSession,
          customer: opts.customer,
          billingAddress: opts.billingAddress,
          shippingAddress: opts.shippingAddress,
          transaction: opts.transaction,
          merchantId: this._merchantId,
          acimaJsClientVersion: sdkVersion,
          initMessage: opts.initMessage,
          bigCommerceCheckout: opts.bigCommerceCheckout,
          ancestorOrigins: this._additionalOrigins,
          isSuccessCallbackMode: opts.isSuccessCallbackMode,
          platform: this._platform,
          source: this._source,
          origin: window.location.origin,
          phpVersion: this._phpVersion,
          pluginVersion: this._pluginVersion,
          databaseVersion: this._databaseVersion,
          searchParams: searchParamsRecord(),
          trustevSessionId: this._trustevSessionId,
          neuroidSessionId: this._neuroidSessionId,
          analyticCorrelationId: this._analyticCorrelationId,
        }),
        onEventNotification: (message) => {
          if (typeof opts.onEventNotification == "function") {
            opts.onEventNotification(makeEventNotification(message))
          }
        }
      })
    })
  }

  preapproval(opts: PreapprovalOptions): Promise<ApplicationStatusResponse | null> {
    return new Promise((resolve, reject) => {
      this._openIframe({
        onCheckoutSuccessful: () => {
          throw new Error('Preapproval should not get checkout successful message.')
        },
        onInitialPayment: () => {
          throw new Error('Preapproval should not get initial payment message.')
        },
        onLeaseAgreementSigned: () => {
          throw new Error('Preapproval should not get lease agreement signed message.')
        },
        onPreapprovalSuccessful: (result) =>
          resolve(result),
        onPrequalSuccessful: () => { },
        onConditionSatisfiedSuccessful: () => { },
        onClose: (result) => {
          this._closeIframe()
          resolve(null)
          if (typeof opts.onClose == "function") {
            opts.onClose(result)
          }
        },
        onError: (e: ClientError) => reject(e),
        onResize: (e) => {
          if (typeof opts.onResize == "function") {
            opts.onResize({ width: e.width, height: e.height })
          }
        },
        onPageTransition: (result) => {
          if (typeof opts.onPageTransition == "function") {
            opts.onPageTransition({ page: result.page })
          }
        },
        onDenied: (e) => {
          if (typeof opts.onDenied == "function") {
            opts.onDenied(e)
          }
        },
        onCancelled: (e) => {
          if (typeof opts.onCancelled == "function") {
            opts.onCancelled(e)
          }
        },
        onIncomplete: (e) => {
          if (typeof opts.onIncomplete == "function") {
            opts.onIncomplete(e)
          }
        },
        onReady: () => this._postMessage({
          type: 'ACIMA_ECOM_IFRAME_INITIALIZE',
          mode: 'Preapproval',
          enablePolling: opts.enablePolling ?? true,
          containerMode: this._containerMode,
          userCanClose: opts.userCanClose ?? this._userCanClose,
          showDefaultHeader: opts.showDefaultHeader ?? this._showDefaultHeader,
          contractId: opts.contractId,
          authToken: opts.authToken,
          impersonatedSession: opts.impersonatedSession,
          customer: opts.customer,
          billingAddress: opts.billingAddress,
          shippingAddress: opts.shippingAddress,
          merchantId: this._merchantId,
          acimaJsClientVersion: sdkVersion,
          initMessage: opts.initMessage,
          ancestorOrigins: this._additionalOrigins,
          platform: this._platform,
          source: this._source,
          origin: window.location.origin,
          searchParams: searchParamsRecord(),
          trustevSessionId: this._trustevSessionId,
          neuroidSessionId: this._neuroidSessionId,
          analyticCorrelationId: this._analyticCorrelationId,
        }),
        onEventNotification: (message) => {
          if (typeof opts.onEventNotification == "function") {
            opts.onEventNotification(makeEventNotification(message))
          }
        }
      })
    })
  }

  prequal(opts: PrequalOptions): Promise<ApplicationStatusResponse> {
    return new Promise((resolve, reject) => {
      this._openIframe({
        onCheckoutSuccessful: () => { },
        onInitialPayment: () => { },
        onLeaseAgreementSigned: () => { },
        onPreapprovalSuccessful: (result: ApplicationStatusResponse) =>
          resolve(result),
        onPrequalSuccessful: (result: ApplicationStatusResponse) => {
          if (typeof opts.onPrequalDecision == "function") {
            opts.onPrequalDecision(result)
          }
        },
        onConditionSatisfiedSuccessful: (result: ConditionSatisfiedResponse) => {
          if (typeof opts.onConditionSatisfied == "function") {
            opts.onConditionSatisfied(result)
          }
        },
        onClose: (result?: ClosedResponse) => {
          this._closeIframe()
          reject({ code: 'CheckoutInterrupted', message: 'Checkout was interrupted' })
          if (typeof opts.onClose == "function") {
            opts.onClose(result)
          }
        },
        onError: (e: ClientError) => reject(e),
        onResize: (e) => {
          if (typeof opts.onResize == "function") {
            opts.onResize({ width: e.width, height: e.height })
          }
        },
        onPageTransition: (result) => {
          if (typeof opts.onPageTransition == "function") {
            opts.onPageTransition({ page: result.page })
          }
        },
        onDenied: (e) => {
          if (typeof opts.onDenied == "function") {
            opts.onDenied(e)
          }
        },
        onCancelled: (e) => {
          if (typeof opts.onCancelled == "function") {
            opts.onCancelled(e)
          }
        },
        onIncomplete: (e) => {
          if (typeof opts.onIncomplete == "function") {
            opts.onIncomplete(e)
          }
        },
        onReady: () => this._postMessage({
          type: 'ACIMA_ECOM_IFRAME_INITIALIZE',
          mode: 'Prequal',
          enablePolling: opts.enablePolling ?? true,
          containerMode: this._containerMode,
          userCanClose: opts.userCanClose ?? this._userCanClose,
          showDefaultHeader: opts.showDefaultHeader ?? this._showDefaultHeader,
          contractId: opts.contractId,
          authToken: opts.authToken,
          impersonatedSession: opts.impersonatedSession,
          customer: opts.customer,
          billingAddress: opts.billingAddress,
          shippingAddress: opts.shippingAddress,
          transaction: opts.transaction,
          merchantId: this._merchantId,
          acimaJsClientVersion: sdkVersion,
          initMessage: opts.initMessage,
          ancestorOrigins: this._additionalOrigins,
          platform: this._platform,
          source: this._source,
          phpVersion: this._phpVersion,
          pluginVersion: this._pluginVersion,
          databaseVersion: this._databaseVersion,
          origin: window.location.origin,
          searchParams: searchParamsRecord(),
          trustevSessionId: this._trustevSessionId,
          neuroidSessionId: this._neuroidSessionId,
          analyticCorrelationId: this._analyticCorrelationId,
        }),
        onEventNotification: (message) => {
          if (typeof opts.onEventNotification == "function") {
            opts.onEventNotification(makeEventNotification(message))
          }
        }
      })
    })
  }

  initialize(initOptions: InitializeOptions): void {
    const container = initOptions.buttonContainerElement
    if (container == null) {
      console.log('Acima: buttonContainerElement was not provided, button will not be injected')
      return
    }

    let parentElem: HTMLElement | null = null
    if (container instanceof HTMLElement) {
      parentElem = container
    } else if (typeof container === 'string') {
      parentElem = document.getElementById(container)
    }

    if (!parentElem) {
      console.log(`Acima: could not resolve buttonContainerElement "${container}", button will not be injected`)
      return
    }

    const button = document.createElement('button')
    button.type = 'button'
    button.ariaLabel = 'Apply with Acima'
    button.style.cssText = 'background:none;border:none;padding:0;cursor:pointer;display:inline-block;'
    const img = document.createElement('img')
    img.src = `${this._iframeUrl}/assets/AcimaJsLaunchButton.svg`
    img.alt = ''
    img.style.cssText = 'height:100%;width:auto;display:block;'
    button.appendChild(img)

    button.addEventListener('click', () => {
      let dispatch: Promise<CheckoutResponse | ApplicationStatusResponse | null>
      const { initializationType } = initOptions

      switch (initializationType) {
        case 'checkout':
          dispatch = this.checkout(initOptions.appOptions)
          break
        case 'prequal':
          dispatch = this.prequal(initOptions.appOptions)
          break
        case 'preapproval':
          dispatch = this.preapproval(initOptions.appOptions)
          break
        default:
          console.error(`Acima: unrecognized initializationType "${initializationType}"`)
          return
      }

      dispatch
        .then((response) => { if (initOptions.onSuccess) initOptions.onSuccess(response as CheckoutResponse & ApplicationStatusResponse) })
        .catch((error) => { if (initOptions.onError) initOptions.onError(error) })
    })

    parentElem.appendChild(button)
  }

  private _compact<T>(list: T[]): T[] {
    return list
      .filter((e) => !!e)
      .filter((v, i, a) => a.indexOf(v) === i)
  }

  private _origin(url: string): string {
    const doc = document.implementation.createHTMLDocument('')
    const anchor = doc.createElement('a')
    anchor.href = url

    // Bail if the url was invalid
    if (!anchor.hostname) { return "" }

    const defaultPort = { 'http:': '80', 'https:': '443' }[anchor.protocol]
    let origin = anchor.protocol + '//' + anchor.hostname

    if (anchor.port !== '' && anchor.port !== defaultPort) {
      origin += ':' + anchor.port
    }

    return origin
  }

  private _openIframe(handlers: OpenIframeHandlers): void {
    if (this._activeMessageHandler || this._iframeElement || window.acimaOpenIframe) {
      throw new Error('Iframe already open or improperly closed')
    }

    window.acimaOpenIframe = true


    this._activeMessageHandler = (event: MessageEvent) => {
      if (event.origin !== this._iframeOrigin
        || event?.data?.type?.includes?.("webpack")
        || event?.source !== this._iframeElement?.contentWindow) {
        return null
      }
      const message: InboundMessage = parseInboundOrUnknown(event.data)
      switch (message.type) {
        case 'UNKNOWN_MESSAGE_IGNORE':
          break
        case 'ACIMA_ECOM_IFRAME_CHECKOUT_SUCCESSFUL':
          handlers.onEventNotification(message)
          return handlers.onCheckoutSuccessful({
            leaseId: message.leaseId,
            applicationId: message.applicationId,
            leaseNumber: message.leaseNumber,
            checkoutToken: message.checkoutToken,
            virtualCard: message.virtualCard,
            createOrderComplete: this.createOrderComplete.bind(this),
            approvalAmount: message.approvalAmount
          })

        case 'ACIMA_ECOM_IFRAME_APPLICATION_STATUS':
          handlers.onEventNotification(message)
          switch (message.appStatus) {
            case 'Approved':
              return handlers.onPreapprovalSuccessful(message)
            case 'Prequalified':
              return handlers.onPrequalSuccessful(message)
            case 'Denied':
              return handlers.onDenied(message)
            case 'Incomplete':
              return handlers.onIncomplete(message)
            case 'Cancelled':
              return handlers.onCancelled(message)
            case 'InitialPayment':
              return handlers.onInitialPayment(message)
            case 'LeaseAgreementSigned':
              return handlers.onLeaseAgreementSigned(message)
            default:
              return
          }

        case 'ACIMA_ECOM_IFRAME_CONDITION_SATISFIED':
          handlers.onEventNotification(message)
          return handlers.onConditionSatisfiedSuccessful(message)

        case 'ACIMA_ECOM_IFRAME_CORRECTION_SATISFIED':
          return handlers.onEventNotification(message)

        case 'ACIMA_ECOM_IFRAME_ANALYTICS':
          if (message.page) {
            handlers.onPageTransition({
              page: message.page,
            })
          }
          return

        case 'ACIMA_ECOM_IFRAME_CLOSE':
          handlers.onEventNotification(message)
          this._closeIframe()
          return handlers.onClose({ appStatus: message.appStatus, checkoutToken: message.checkoutToken })

        case 'ACIMA_ECOM_IFRAME_ERROR':
          handlers.onEventNotification(message)
          return handlers.onError({
            code: message.code,
            message: message.message,
            leaseId: message.leaseId,
            appStatus: message.appStatus
          })

        case 'ACIMA_ECOM_IFRAME_RESIZE':
          return handlers.onResize({
            width: message.width,
            height: message.height
          })

        case 'ACIMA_ECOM_IFRAME_READY':
          handlers.onEventNotification(message)
          return handlers.onReady()

        default:
          return null
      }
    }
    window.addEventListener('message', this._activeMessageHandler)

    this._iframeElement = document.createElement('iframe')
    this._iframeElement.setAttribute('id', 'iframe')
    this._iframeElement.setAttribute('src', this._iframeUrl)
    this._iframeElement.setAttribute('allowtransparency', 'true')
    this._iframeElement.style.zIndex = '1000'
    this._iframeElement.style.width = '100%'
    this._iframeElement.style.height = '100%'
    this._iframeElement.style.border = 'none'

    if (this._launchAsModal) {
      this._iframeElement.style.position = 'fixed'
      this._iframeElement.style.top = '0'
      this._iframeElement.style.left = '0'
    }

    this._parentElem.appendChild(this._iframeElement)
  }

  private _closeIframe(): void {
    window.acimaOpenIframe = false
    if (this._activeMessageHandler) {
      window.removeEventListener('message', this._activeMessageHandler)
      this._activeMessageHandler = null
    }

    if (this._iframeElement) {
      this._parentElem.removeChild(this._iframeElement)
      this._iframeElement = null
    }
  }

  private _postMessage(message: OutboundMessage): void {
    if (!this._iframeElement || !this._iframeElement.contentWindow) {
      throw new Error('Cannot post message while iframe closed.')
    }

    const str = JSON.stringify(message)
    this._iframeElement.contentWindow.postMessage(str, this._iframeOrigin)
  }
}



export type ClientOptions = {
  merchantId: ID,
  iframeUrl?: string | null,
  iframeContainer?: HTMLElement | string | null,
  ancestorOrigins?: string[],
  platform?: Platform,
  source?: Source,
  phpVersion?: string | null,
  pluginVersion?: string | null,
  databaseVersion?: string | null,
  trustevSessionId?: string,
  neuroidSessionId?: NeuroidSession,
  analyticCorrelationId?: string,
}

export type PreapprovalOptions = {
  enablePolling?: boolean,
  userCanClose?: boolean,
  showDefaultHeader?: boolean,
  contractId?: string,
  authToken?: string,
  impersonatedSession?: ImpersonatedSession,
  customer?: DeepPartial<Customer>,
  billingAddress?: DeepPartial<Address>,
  shippingAddress?: DeepPartial<Address>,
  initMessage?: string
  onClose?: (e: ClosedResponse) => void,
  onResize?: (e: ResizeResponse) => void,
  onPageTransition?: (e: PageTransitionResponse) => void,
  onDenied?: (e: ApplicationStatusResponse) => void,
  onCancelled?: (e: ApplicationStatusResponse) => void,
  onIncomplete?: (e: ApplicationStatusResponse) => void,
  onConditionSatisfied?: (e: ConditionSatisfiedResponse) => void,
  onEventNotification?: (e: EventNotification) => void
}

export type CheckoutOptions = {
  enablePolling?: boolean,
  userCanClose?: boolean,
  showDefaultHeader?: boolean,
  contractId?: string,
  authToken?: string,
  impersonatedSession?: ImpersonatedSession,
  customer?: DeepPartial<Customer>,
  billingAddress?: DeepPartial<Address>,
  shippingAddress?: DeepPartial<Address>,
  transaction: Transaction,
  initMessage?: string,
  bigCommerceCheckout?: BigComCheckout,
  isSuccessCallbackMode?: boolean,
  onClose?: (e?: ClosedResponse) => void,
  onResize?: (e: ResizeResponse) => void,
  onInitialPayment?: (e: ApplicationStatusResponse) => void
  onLeaseAgreementSigned?: (e: ApplicationStatusResponse) => void
  onPageTransition?: (e: PageTransitionResponse) => void,
  onPreapprovalSuccessful?: (e: ApplicationStatusResponse) => void,
  onDenied?: (e: ApplicationStatusResponse) => void,
  onCancelled?: (e: ApplicationStatusResponse) => void,
  onIncomplete?: (e: ApplicationStatusResponse) => void,
  onConditionSatisfied?: (e: ConditionSatisfiedResponse) => void,
  onEventNotification?: (e: EventNotification) => void
}

export type PrequalOptions = {
  enablePolling?: boolean,
  userCanClose?: boolean,
  showDefaultHeader?: boolean,
  contractId?: string,
  authToken?: string,
  impersonatedSession?: ImpersonatedSession,
  customer?: DeepPartial<Customer>,
  billingAddress?: DeepPartial<Address>,
  shippingAddress?: DeepPartial<Address>,
  transaction: Transaction,
  initMessage?: string
  onClose?: (e?: ClosedResponse) => void,
  onResize?: (e: ResizeResponse) => void,
  onPageTransition?: (e: PageTransitionResponse) => void,
  bigCommerceCheckout?: BigComCheckout,
  isSuccessCallbackMode?: boolean,
  onPrequalDecision?: (e: ApplicationStatusResponse) => void,
  onConditionSatisfied?: (e: ConditionSatisfiedResponse) => void,
  onDenied?: (e: ApplicationStatusResponse) => void,
  onCancelled?: (e: ApplicationStatusResponse) => void,
  onIncomplete?: (e: ApplicationStatusResponse) => void,
  onEventNotification?: (e: EventNotification) => void
}

export type CheckoutResponse = {
  applicationId: ID,
  leaseId: ID,
  leaseNumber: number,
  checkoutToken?: CheckoutToken,
  virtualCard?: DirectVirtualCard,
  createOrderComplete: () => void
  approvalAmount?: number, // In Cents
}

export type ClosedResponse = {
  appStatus?: string,
  checkoutToken?: string,
}

export type ApplicationStatusResponse = {
  applicationId: ID,
  appStatus: string,
  approvalAmount: number, // In Cents
  approvalUsed: boolean
  leaseNumber?: number,
  impersonatedSessionUserId?: ID,
  ecomSessionId: string,
}

export type ConditionSatisfiedResponse = {
  leaseId: ID,
  impersonatedSessionUserId?: ID,
  ecomSessionId?: string,
  conditionType: string
}

export type ResizeResponse = {
  width: number,
  height: number,
}

export type PageTransitionResponse = {
  page: string,
}

export type InitializeOptions =
  | {
      initializationType: 'checkout'
      appOptions: CheckoutOptions
      buttonContainerElement?: HTMLElement | string | null
      onSuccess?: (response: CheckoutResponse) => void
      onError?: (error: unknown) => void
    }
  | {
      initializationType: 'prequal'
      appOptions: PrequalOptions
      buttonContainerElement?: HTMLElement | string | null
      onSuccess?: (response: ApplicationStatusResponse) => void
      onError?: (error: unknown) => void
    }
  | {
      initializationType: 'preapproval'
      appOptions: PreapprovalOptions
      buttonContainerElement?: HTMLElement | string | null
      onSuccess?: (response: ApplicationStatusResponse | null) => void
      onError?: (error: unknown) => void
    }

export type ClientErrorCode = IframeErrorCode | 'CheckoutInterrupted'

export type ClientError = {
  code: ClientErrorCode
  message: string
  leaseId?: ID
  appStatus?: string
}

type OpenIframeHandlers = {
  onCheckoutSuccessful: (result: CheckoutResponse) => void,
  onPreapprovalSuccessful: (result: ApplicationStatusResponse) => void,
  onPrequalSuccessful: (result: ApplicationStatusResponse) => void,
  onConditionSatisfiedSuccessful: (result: ConditionSatisfiedResponse) => void,
  onClose: (result: ClosedResponse) => void,
  onInitialPayment: (result: ApplicationStatusResponse) => void,
  onLeaseAgreementSigned: (result: ApplicationStatusResponse) => void,
  onReady: () => void,
  onError: (e: ClientError) => void,
  onResize: (result: ResizeResponse) => void,
  onPageTransition: (result: PageTransitionResponse) => void,
  onDenied: (result: ApplicationStatusResponse) => void,
  onCancelled: (result: ApplicationStatusResponse) => void,
  onIncomplete: (result: ApplicationStatusResponse) => void,
  onEventNotification: (result: InboundMessage) => void,
}

type MessageListener = (event: MessageEvent) => void

type EventNotification = {
  type: string,
  leaseId?: ID,
  leaseNumber?: number,
  contractStatus?: string,
  error?: Error,
}

type Error = {
  errorType: string,
  errorMessage: string,
}

function makeEventNotification(message: InboundMessage): EventNotification {
  switch (message.type) {
    case 'ACIMA_ECOM_IFRAME_CHECKOUT_SUCCESSFUL':
      return {
        type: 'ACIMA_ECOM_IFRAME_CHECKOUT_SUCCESSFUL',
        leaseId: message.leaseId,
        leaseNumber: message.leaseNumber,
        contractStatus: message.appStatus,
      }
    case 'ACIMA_ECOM_IFRAME_APPLICATION_STATUS':
      return {
        type: 'ACIMA_ECOM_IFRAME_APPLICATION_STATUS',
        leaseId: message.applicationId,
        leaseNumber: message.leaseNumber,
        contractStatus: message.appStatus,
      }
    case 'ACIMA_ECOM_IFRAME_CONDITION_SATISFIED':
      return {
        type: 'ACIMA_ECOM_IFRAME_CONDITION_SATISFIED',
        leaseId: message.leaseId,
        contractStatus: message.appStatus,
      }
    case 'ACIMA_ECOM_IFRAME_CORRECTION_SATISFIED':
      return {
        type: 'ACIMA_ECOM_IFRAME_CORRECTION_SATISFIED',
        leaseId: message.leaseId,
        contractStatus: message.appStatus,
      }
    case 'ACIMA_ECOM_IFRAME_ERROR':
      return {
        type: 'ACIMA_ECOM_IFRAME_ERROR',
        leaseId: message.leaseId,
        contractStatus: message.appStatus,
        error: {
          errorType: message.code,
          errorMessage: message.message,
        }
      }
    default:
      if (message.type.startsWith('ACIMA_ECOM_IFRAME')) {
        return { type: message.type }
      }
      else {
        return {
          type: 'UNKNOWN_MESSAGE_IGNORE',
        }
      }
  }
}

function parseInboundOrUnknown(data: unknown): InboundMessage {
  const unknownMessage: UnknownMessage = {
    type: 'UNKNOWN_MESSAGE_IGNORE',
    data: data,
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data)
    }
    catch (err) {
      return unknownMessage
    }
  }
  else {
    return unknownMessage
  }
}